import { v4 as uuidv4 } from "uuid";

/**
 * Tree helpers for BoQ: nodes have optional children array.
 * Tree = array of root nodes.
 */

/**
 * @param {Array} tree - Root nodes
 * @returns {number}
 */
export const countRootNodes = (tree) => (Array.isArray(tree) ? tree.length : 0);

/**
 * Count root nodes that have an OZ/item number (used for sequential numbering; excludes Additional Text, NOTE, etc.).
 * @param {Array} tree - Root nodes
 * @returns {number}
 */
export const countNumberedRootNodes = (tree) =>
	Array.isArray(tree)
		? tree.filter((n) => n?.itemNumber != null && n.itemNumber !== "")
				.length
		: 0;

/**
 * Position number (OZ) generation logic:
 *
 * - Root level (parentItemNumber = null): next number is 01, 02, 03, ... (2 digits).
 * - Under a root (e.g. "01"): children get 01.01, 01.02, 01.03, ... (parent + 2-digit suffix).
 * - Deeper (e.g. "01.01"): next level gets 01.01.001, 01.01.002, ... (3-digit suffix).
 *
 * siblingCount = number of existing siblings *before* adding the new one, so next = siblingCount + 1.
 * Only "numberable" rows (Group, Subgroup, Position, Execution description) get a number;
 * NOTE and Additional Text use itemNumber = null and are excluded from counts (countNumberedRootNodes).
 *
 * @param {string|null} parentItemNumber - Parent's OZ (e.g. "01", "01.01") or null for root
 * @param {number} siblingCount - Number of existing siblings (before adding)
 * @returns {string} Next OZ string
 */
export const getNextItemNumber = (parentItemNumber, siblingCount) => {
	const next = siblingCount + 1;
	if (!parentItemNumber) {
		return String(next).padStart(2, "0");
	}
	const parts = parentItemNumber.split(".");
	if (parts.length === 1) {
		return `${parentItemNumber}.${String(next).padStart(2, "0")}`;
	}
	if (parts.length === 2) {
		return `${parentItemNumber}.${String(next).padStart(3, "0")}`;
	}
	return `${parentItemNumber}.${String(next).padStart(3, "0")}`;
};

/**
 * Count all nodes in the tree with the given type (recursive).
 * @param {Array} tree - Root nodes
 * @param {string} nodeType - type field to match (e.g. "execution_description")
 * @returns {number}
 */
export const countNodesByType = (tree, nodeType) => {
	if (!Array.isArray(tree)) return 0;
	let count = 0;
	for (const node of tree) {
		if (node?.type === nodeType) count += 1;
		if (Array.isArray(node?.children)) {
			count += countNodesByType(node.children, nodeType);
		}
	}
	return count;
};

/**
 * Next item number for Execution Description: flat sequence 0001, 0002, 0003, ...
 * No hierarchy; counts all execution_description nodes in the tree and returns (count + 1) as 4 digits.
 * @param {Array} tree - Root nodes
 * @returns {string}
 */
export const getNextExecutionDescriptionNumber = (tree) => {
	const count = countNodesByType(tree, "execution_description");
	return String(count + 1).padStart(4, "0");
};

/**
 * Find a node by id in tree (recursive).
 * @returns {{ node, parent, indexInParent } | null}
 */
/**
 * Returns the IDs of all ancestor nodes (root → direct parent) for the node with the given
 * `targetId`. Returns an empty array if the node is at the root level.
 * Returns `null` if the node is not found anywhere in the tree.
 */
export const findAncestorIds = (tree, targetId, _ancestors = []) => {
	if (!Array.isArray(tree)) return null;
	for (const node of tree) {
		if (node?.id === targetId) return _ancestors;
		if (Array.isArray(node?.children) && node.children.length > 0) {
			const found = findAncestorIds(node.children, targetId, [
				..._ancestors,
				node.id,
			]);
			if (found !== null) return found;
		}
	}
	return null;
};

export const findNodeInTree = (
	tree,
	nodeId,
	parent = null,
	siblings = null,
) => {
	if (!Array.isArray(tree)) return null;
	for (let i = 0; i < tree.length; i++) {
		const node = tree[i];
		if (node?.id === nodeId) {
			return {
				node,
				parent,
				indexInParent: i,
				siblings: siblings ?? tree,
			};
		}
		if (Array.isArray(node?.children)) {
			const found = findNodeInTree(
				node.children,
				nodeId,
				node,
				node.children,
			);
			if (found) return found;
		}
	}
	return null;
};

/** Returns true if targetId is the same as node or is in node's subtree. */
const isInSubtree = (node, targetId) => {
	if (!node) return false;
	if (node.id === targetId) return true;
	return (node.children || []).some((c) => isInSubtree(c, targetId));
};

/**
 * Check if nodeId is the same as ancestorId or is a descendant of ancestorId in the tree.
 * @param {Array} tree - Root nodes
 * @param {string} nodeId - Node to test
 * @param {string} ancestorId - Potential ancestor
 * @returns {boolean}
 */
export const isDescendantOf = (tree, nodeId, ancestorId) => {
	const found = findNodeInTree(tree, ancestorId);
	return found ? isInSubtree(found.node, nodeId) : false;
};

/**
 * Remove a node by id (and its children). Returns new tree.
 * @param {Array} tree - Root nodes
 * @param {string} nodeId - Id of the node to remove
 * @returns {Array} New tree without the node
 */
export const removeNodeFromTree = (tree, nodeId) => {
	if (!Array.isArray(tree)) return tree;
	const idx = tree.findIndex((n) => n?.id === nodeId);
	if (idx !== -1) {
		return tree.filter((_, i) => i !== idx);
	}
	return tree.map((node) => {
		if (!Array.isArray(node.children)) return node;
		const newChildren = removeNodeFromTree(node.children, nodeId);
		if (newChildren !== node.children) {
			return { ...node, children: newChildren };
		}
		return node;
	});
};

/**
 * Update a node by id (immutable). Returns new tree.
 */
export const updateNodeInTree = (tree, nodeId, updates) => {
	if (!Array.isArray(tree)) return tree;
	return tree.map((node) => {
		if (node.id === nodeId) {
			return { ...node, ...updates };
		}
		const children = node.children;
		if (Array.isArray(children)) {
			const newChildren = updateNodeInTree(children, nodeId, updates);
			if (newChildren !== children) {
				return { ...node, children: newChildren };
			}
		}
		return node;
	});
};

/**
 * Generate a new unique id for a node.
 * @returns {string}
 */
const generateNodeId = () => uuidv4();

/**
 * Deep clone a node and all its descendants, assigning new ids throughout.
 * Used for copy/paste so pasted nodes have unique ids.
 * @param {Object} node - Tree node (with optional children)
 * @returns {Object} Cloned node with new id and cloned children
 */
export const cloneNodeWithNewIds = (node) => {
	if (!node) return null;
	const id = generateNodeId();
	const children = Array.isArray(node.children)
		? node.children.map((child) => cloneNodeWithNewIds(child))
		: [];
	return {
		...node,
		id,
		children,
	};
};

/**
 * Renumber itemNumbers in a pasted subtree (mutates nodes in place).
 * - NOTE: itemNumber = null
 * - execution_description: flat sequence from execDescCounter (0001, 0002, ...)
 * - Others: hierarchical getNextItemNumber(parentItemNumber, siblingIndex)
 * @param {Array} children - Array of child nodes (mutated)
 * @param {string|null} parentItemNumber - Parent's OZ for hierarchical numbering
 * @param {{ next: number }} execDescCounter - Mutable counter for execution_description sequence
 */
export const renumberPastedSubtree = (
	children,
	parentItemNumber,
	execDescCounter,
) => {
	if (!Array.isArray(children)) return;
	children.forEach((child, index) => {
		if (child.type === "note") {
			child.itemNumber = null;
		} else if (child.type === "execution_description") {
			child.itemNumber = String(execDescCounter.next).padStart(4, "0");
			execDescCounter.next += 1;
		} else {
			child.itemNumber = getNextItemNumber(parentItemNumber, index);
		}
		renumberPastedSubtree(
			child.children,
			child.itemNumber,
			execDescCounter,
		);
	});
};

/**
 * Add a new node after a sibling (same level).
 * @param {Array} tree - Root nodes
 * @param {string} siblingId - The existing node to insert after
 * @param {Object} newNode - Node to add
 * @returns {Array} New tree
 */
export const addNodeAfterSibling = (tree, siblingId, newNode) => {
	if (!Array.isArray(tree)) return tree;
	const withChildren = { ...newNode, children: newNode.children ?? [] };

	// Check at this level
	const idx = tree.findIndex((n) => n.id === siblingId);
	if (idx !== -1) {
		const copy = [...tree];
		copy.splice(idx + 1, 0, withChildren);
		return copy;
	}

	// Recurse into children
	return tree.map((node) => {
		if (!Array.isArray(node.children)) return node;
		const newChildren = addNodeAfterSibling(
			node.children,
			siblingId,
			newNode,
		);
		if (newChildren !== node.children) {
			return { ...node, children: newChildren };
		}
		return node;
	});
};

/**
 * Add a new node: at root (parentId null) at index, or as child of parentId.
 * @param {Array} tree - Root nodes
 * @param {string|null} parentId - Parent node id, or null for root
 * @param {Object} newNode - Node to add (must have id, type, itemNumber, etc.)
 * @param {"start"|"end"|null} rootPosition - If adding at root: "start" or "end". Ignored if parentId set.
 * @returns {Array} New tree
 */
export const addNodeToTree = (
	tree,
	parentId,
	newNode,
	rootPosition = "end",
) => {
	const safeTree = Array.isArray(tree) ? [...tree] : [];

	if (parentId == null) {
		// Add at root
		const withChildren = { ...newNode, children: newNode.children ?? [] };
		if (rootPosition === "start") {
			return [withChildren, ...safeTree];
		}
		return [...safeTree, withChildren];
	}

	const found = findNodeInTree(safeTree, parentId);
	if (!found) {
		const withChildren = { ...newNode, children: newNode.children ?? [] };
		return [...safeTree, withChildren];
	}
	// If parent is root (found.parent is null), we still have found.node = the root node; we need to add as child of that node. So parentId is the id of the node we're adding under. So found.node is the parent.
	const parentNode = found.node;
	const existingChildren = Array.isArray(parentNode.children)
		? parentNode.children
		: [];
	const withChildren = { ...newNode, children: newNode.children ?? [] };
	return updateNodeInTree(safeTree, parentId, {
		children: [...existingChildren, withChildren],
	});
};

/**
 * Insert a node as sibling of an existing node (before or after).
 * @param {Array} tree - Root nodes
 * @param {string} siblingId - Id of the sibling to insert before/after
 * @param {Object} node - Node to insert (with id, children, etc.)
 * @param {"before"|"after"} placement
 * @returns {Array} New tree
 */
const insertNodeAsSibling = (tree, siblingId, node, placement) => {
	const found = findNodeInTree(tree, siblingId);
	if (!found) return tree;
	const parent = found.parent;
	const siblings = found.siblings;
	const index = siblings.findIndex((n) => n?.id === siblingId);
	if (index === -1) return tree;
	const insertIndex = placement === "before" ? index : index + 1;
	const withChildren = { ...node, children: node.children ?? [] };

	if (!parent) {
		// Root level
		const copy = [...tree];
		copy.splice(insertIndex, 0, withChildren);
		return copy;
	}
	return updateNodeInTree(tree, parent.id, {
		children: [
			...siblings.slice(0, insertIndex),
			withChildren,
			...siblings.slice(insertIndex),
		],
	});
};

/**
 * Move a node to before/after a target node (same level) or inside a container node.
 * Cannot move a node into its own descendant.
 * @param {Array} tree - Root nodes
 * @param {string} dragId - Id of the node to move
 * @param {string} targetId - Id of the target node
 * @param {"before"|"after"|"inside"} placement
 * @returns {Array} New tree
 */
export const moveNodeInTree = (tree, dragId, targetId, placement) => {
	if (dragId === targetId) return tree;
	const dragFound = findNodeInTree(tree, dragId);
	const targetFound = findNodeInTree(tree, targetId);
	if (!dragFound?.node || !targetFound?.node) return tree;
	if (isDescendantOf(tree, targetId, dragId)) return tree; // cannot move parent onto descendant

	const treeWithoutDrag = removeNodeFromTree(tree, dragId);

	if (placement === "inside") {
		// Reparent: append dragNode as the last child of targetId
		return addNodeToTree(treeWithoutDrag, targetId, dragFound.node, "end");
	}

	// After removal, target might still be in tree; find it again to get current siblings list
	const targetFoundAfter = findNodeInTree(treeWithoutDrag, targetId);
	if (!targetFoundAfter) return tree;

	return insertNodeAsSibling(
		treeWithoutDrag,
		targetId,
		dragFound.node,
		placement,
	);
};

/**
 * Recursively renumber itemNumbers for the whole tree (immutable).
 * NOTE -> null; execution_description -> 0001, 0002, ...; others -> hierarchical.
 * @param {Array} nodes - Root nodes
 * @param {{ next: number }} execDescCounter - Mutable counter for execution_description (start at 1)
 * @param {string|null} parentItemNumber - Parent OZ for hierarchical numbering
 * @returns {Array} New array of nodes with itemNumber set
 */
const renumberNodes = (nodes, execDescCounter, parentItemNumber) => {
	if (!Array.isArray(nodes)) return [];
	return nodes.map((node, index) => {
		let itemNumber;
		if (node.type === "note") {
			itemNumber = null;
		} else if (node.type === "execution_description") {
			itemNumber = String(execDescCounter.next).padStart(4, "0");
			execDescCounter.next += 1;
		} else {
			itemNumber = getNextItemNumber(parentItemNumber, index);
		}
		const children = renumberNodes(
			node.children,
			execDescCounter,
			itemNumber,
		);
		return { ...node, itemNumber, children };
	});
};

/**
 * Renumber the entire tree: all itemNumbers recalculated.
 * @param {Array} tree - Root nodes
 * @returns {Array} New tree
 */
export const renumberTree = (tree) => {
	if (!Array.isArray(tree)) return tree;
	const execDescCounter = { next: 1 };
	return renumberNodes(tree, execDescCounter, null);
};
